import { cn } from "@/lib/utils";
import {
  BRAND_ICON_NAMES,
  BRAND_MARKS,
  type BrandIconName,
} from "./_generated/brand-icon.data";

export { BRAND_ICON_NAMES, type BrandIconName };

/**
 * The only way to render a third-party brand mark.
 *
 * Marks live in `packages/nf-design-system/icons/brand/`, downloaded from the
 * vendors' own sources and normalized by that package's `icons:brand` script.
 * Never inline, redraw, or approximate a brand mark anywhere else — a mark
 * that is close but not exact is someone's trademark rendered wrong, and CI
 * fails on it.
 *
 *   <BrandIcon name="slack" className="h-4 w-4" />
 *   <BrandIcon name="applovin" variant="mono" className="h-4 w-4" />
 *
 * `color` keeps the brand's palette. `mono` paints with `currentColor`, for
 * places where a brand palette would fight the surface.
 */
export function BrandIcon({
  name,
  variant = "color",
  className,
  title,
}: {
  name: BrandIconName;
  variant?: "color" | "mono";
  className?: string;
  /** Give the mark an accessible name; omit it when a label sits beside it. */
  title?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={cn("shrink-0", className)}
    >
      {BRAND_MARKS[name][variant]}
    </svg>
  );
}

/** Narrowing helper for call sites holding a string from data. */
export function isBrandIconName(value: string): value is BrandIconName {
  return (BRAND_ICON_NAMES as string[]).includes(value);
}
